import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { PartnerClient, PartnerService } from '../../../../services/partner.service';

/**
 * Sends the client the link to take its account over again. Until it has, the
 * address can still be corrected here (a typo would send the account to a stranger).
 */
@Component({
  selector: 'app-claim-mail-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmDialogImports,
    HlmFieldImports,
    HlmInputImports,
    HlmSpinnerImports,
  ],
  providers: [provideIcons({ lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './claim-mail-dialog.component.html',
})
export class ClaimMailDialogComponent {
  private readonly partnerService = inject(PartnerService);

  /** The client to mail; null keeps the dialog closed. */
  readonly client = input<PartnerClient | null>(null);
  readonly close = output<void>();
  readonly sent = output<PartnerClient>();

  readonly loading = signal(false);
  readonly submitted = signal(false);
  readonly error = signal<string | null>(null);

  readonly form = inject(FormBuilder).nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  constructor() {
    // Start from the address on file each time the dialog opens
    effect(() => {
      const client = this.client();
      if (client) {
        this.form.reset({ email: client.email ?? '' });
        this.submitted.set(false);
        this.error.set(null);
      }
    });
  }

  get emailInvalid(): boolean {
    return this.submitted() && this.form.controls.email.invalid;
  }

  onDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed' && this.client()) this.dismiss();
  }

  dismiss(): void {
    if (!this.loading()) this.close.emit();
  }

  submit(): void {
    const client = this.client();
    if (this.loading() || !client?.public_id) return;
    this.submitted.set(true);
    this.error.set(null);
    if (this.form.invalid) return;

    const email = this.form.getRawValue().email.trim().toLowerCase();
    this.loading.set(true);
    this.partnerService
      .resendClaimMail(client.public_id, email === client.email ? null : email)
      .subscribe({
        next: (updated) => {
          this.loading.set(false);
          this.sent.emit(updated);
        },
        error: (err: HttpErrorResponse) => {
          this.loading.set(false);
          this.error.set(
            err.status === 409
              ? err.error?.detail
              : 'Die E-Mail konnte nicht gesendet werden. Bitte versuche es erneut.',
          );
        },
      });
  }
}
